/**
 * Seasonal Profiles Tab - Kept from SeasonalAdjustment, with cosmetics-relevant labels
 * Removed: Weather Adjustment tab
 * Replaced: Beverage/Ice Cream/Hot Drink profiles → Makeup Summer, Fragrance Winter, Standard
 */
import React, { useState } from 'react';
import {
  Box, Typography, Paper, Button, Grid, TextField, Select, MenuItem,
  FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Chip,
} from '@mui/material';
import { Refresh, Add } from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { useForecastingFilters } from './ForecastingFilterContext';

const COSMETICS_CATEGORIES = [
  'Skincare',
  'Fragrance',
  'Makeup',
  'Haircare',
  'Body Care',
  'Sun Care',
  'General',
];

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const SeasonalProfilesTab: React.FC = () => {
  const { categories, brands } = useForecastingFilters();
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const [newProfileCategory, setNewProfileCategory] = useState('');
  const [newProfileDescription, setNewProfileDescription] = useState('');
  const [monthlyFactors, setMonthlyFactors] = useState<Record<number, number>>({
    1: 1.0, 2: 1.0, 3: 1.0, 4: 1.0, 5: 1.0, 6: 1.0,
    7: 1.0, 8: 1.0, 9: 1.0, 10: 1.0, 11: 1.0, 12: 1.0,
  });
  const [dialogError, setDialogError] = useState<string | null>(null);

  /* Auto-load profiles, refetch whenever the brand/category filters change so the
     trend chart always reflects the active filter selection. */
  const activeCategory = categories.length > 0 ? categories[0] : undefined;
  const activeBrand = brands.length > 0 ? brands[0] : undefined;

  const {
    data: profiles,
    isFetching: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['seasonal-profiles', activeCategory, activeBrand],
    queryFn: async () => {
      const response = await api.getSeasonalProfiles(activeCategory, activeBrand);
      return response.data;
    },
    staleTime: 60_000,
  });
  const error = queryError ? ((queryError as any)?.response?.data?.detail || 'Failed to load seasonal profiles') : null;

  const handleCreateProfile = async () => {
    if (!newProfileName || !newProfileCategory) {
      setDialogError('Please provide profile name and category');
      return;
    }
    setDialogError(null);
    try {
      await api.createSeasonalProfile({
        profile_name: newProfileName,
        product_category: newProfileCategory,
        monthly_factors: monthlyFactors,
        description: newProfileDescription,
      });
      setShowProfileDialog(false);
      setDialogError(null);
      setNewProfileName('');
      setNewProfileCategory('');
      setNewProfileDescription('');
      setMonthlyFactors({ 1: 1.0, 2: 1.0, 3: 1.0, 4: 1.0, 5: 1.0, 6: 1.0, 7: 1.0, 8: 1.0, 9: 1.0, 10: 1.0, 11: 1.0, 12: 1.0 });
      refetch();
    } catch (err: any) {
      setDialogError(err.response?.data?.detail || 'Failed to create profile');
    }
  };

  // Pre-fill cosmetics templates
  const applyTemplate = (template: string) => {
    switch (template) {
      case 'Makeup Summer':
        setNewProfileName('Makeup Summer');
        setNewProfileCategory('Makeup');
        setNewProfileDescription('Summer makeup demand pattern - peaks during Eid & holiday season');
        setMonthlyFactors({ 1: 0.8, 2: 0.9, 3: 1.0, 4: 1.3, 5: 1.2, 6: 1.4, 7: 1.3, 8: 1.1, 9: 0.9, 10: 0.8, 11: 1.0, 12: 1.3 });
        break;
      case 'Fragrance Winter':
        setNewProfileName('Fragrance Winter');
        setNewProfileCategory('Fragrance');
        setNewProfileDescription('Fragrance gifting season - peaks in winter/holiday months');
        setMonthlyFactors({ 1: 1.0, 2: 1.1, 3: 0.9, 4: 0.8, 5: 0.8, 6: 0.7, 7: 0.7, 8: 0.8, 9: 0.9, 10: 1.0, 11: 1.3, 12: 1.5 });
        break;
      case 'Standard':
        setNewProfileName('Standard');
        setNewProfileCategory('General');
        setNewProfileDescription('Flat seasonal pattern - no significant seasonal variation');
        setMonthlyFactors({ 1: 1.0, 2: 1.0, 3: 1.0, 4: 1.0, 5: 1.0, 6: 1.0, 7: 1.0, 8: 1.0, 9: 1.0, 10: 1.0, 11: 1.0, 12: 1.0 });
        break;
    }
  };

  const hasResults = profiles?.profiles && profiles.profiles.length > 0;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Seasonal Profiles</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Define seasonal demand patterns for cosmetics categories. These profiles adjust forecast values by month.
        Select a brand and/or category in the filter bar above to see brand-specific seasonality computed from
        the last 24 months of sales.
      </Typography>

      {/* Active filter context - shows what's driving the displayed trends */}
      {(activeBrand || activeCategory) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">Showing trend for:</Typography>
          {activeBrand && <Chip size="small" label={`Brand: ${activeBrand}`} color="primary" variant="outlined" />}
          {activeCategory && <Chip size="small" label={`Category: ${activeCategory}`} color="primary" variant="outlined" />}
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Button variant="contained" startIcon={<Refresh />} onClick={() => refetch()} fullWidth sx={{ height: '56px' }}>
            Refresh Profiles
          </Button>
        </Grid>
        <Grid item xs={12} md={6}>
          <Button variant="outlined" startIcon={<Add />} onClick={() => { setDialogError(null); setShowProfileDialog(true); }} fullWidth sx={{ height: '56px' }}>
            Create Profile
          </Button>
        </Grid>
      </Grid>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      )}

      {!loading && !hasResults && !error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No seasonal profile available for the current filter selection. Try a different brand or category.
        </Alert>
      )}

      {hasResults && (
        <Grid container spacing={2}>
          {profiles.profiles.map((profile: any) => (
            <Grid item xs={12} md={6} key={profile.profile_id}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6">{profile.profile_name}</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                  {profile.product_brand && <Chip size="small" label={profile.product_brand} color="primary" />}
                  {profile.product_category && <Chip size="small" label={profile.product_category} variant="outlined" />}
                </Box>
                <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">{profile.description}</Typography>
                <ResponsiveContainer width="100%" height={150}>
                  <LineChart data={monthNames.map((name, idx) => ({ month: name, factor: profile.monthly_factors[idx + 1] }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis domain={[0, 'auto']} />
                    <Tooltip />
                    <Line type="monotone" dataKey="factor" stroke="#8B2438" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Profile Dialog */}
      <Dialog open={showProfileDialog} onClose={() => setShowProfileDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Seasonal Profile</DialogTitle>
        <DialogContent>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError(null)}>{dialogError}</Alert>}
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="body2" gutterBottom>Quick Templates:</Typography>
            {['Makeup Summer', 'Fragrance Winter', 'Standard'].map((tmpl) => (
              <Button key={tmpl} variant="outlined" size="small" sx={{ mr: 1, mb: 1 }} onClick={() => applyTemplate(tmpl)}>
                {tmpl}
              </Button>
            ))}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Profile Name" value={newProfileName} onChange={(e) => setNewProfileName(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select value={newProfileCategory} onChange={(e) => setNewProfileCategory(e.target.value)} label="Category">
                  {COSMETICS_CATEGORIES.map((cat) => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" value={newProfileDescription} onChange={(e) => setNewProfileDescription(e.target.value)} multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
                <strong>Monthly Adjustment Factors</strong> (1.0 = average, &gt;1.0 = above, &lt;1.0 = below)
              </Typography>
            </Grid>
            {monthNames.map((name, idx) => (
              <Grid item xs={6} md={2} key={idx}>
                <TextField
                  fullWidth type="number" label={name} value={monthlyFactors[idx + 1]}
                  onChange={(e) => setMonthlyFactors({ ...monthlyFactors, [idx + 1]: parseFloat(e.target.value) || 1.0 })}
                  inputProps={{ step: 0.1, min: 0.1, max: 3.0 }} size="small"
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowProfileDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateProfile} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SeasonalProfilesTab;
